import { useCentralStore } from "../../store/Central";
import { useState } from "react";
import { Unplug, Mail, Building2, Users } from "lucide-react";
import { SectionForNotConnected } from "./section-for-not-connect.component";
import { usePostHog } from "posthog-js/react";
import { ConnectionNextStepsCard } from "./connection-next-steps-card.component";
import { useAttributesStore } from "../../store/Attributes/attributes.store";
import { Button } from "../../components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "../../components/ui/dialog";

export const ConnectedInfo = ({ clientId }: { clientId: string }) => {
	const {
		clientsLookup,
		disconnectClient,
		connectedOrganizationEmail,
		connectedOrganizationType,
	} = useCentralStore((state) => ({
		clientsLookup: state.clientsLookup,
		disconnectClient: state.disconnectClient,
		connectedOrganizationEmail: state.connectedOrganizationEmail,
		connectedOrganizationType: state.connectedOrganizationType,
	}));

	const nextStepsDismissed = useAttributesStore(
		(state) => state.getAttribute("connect_next_steps_dismissed") === 1
	);
	const [warningDialogOpen, setWarningDialogOpen] = useState(false);
	const postHog = usePostHog();
	const connectedOrganizationId =
		clientsLookup[clientId]?.connected_organization_id;
	if (!connectedOrganizationId) {
		return (
			<div className="flex justify-center">
				<SectionForNotConnected />
			</div>
		);
	}

	const connectRelationshipForMultipleClients =
		Object.values(clientsLookup).filter(
			(c) => c.connected_organization_id === connectedOrganizationId
		).length > 1;

	return (
		<div className="flex flex-col gap-5 pt-4">
			{!nextStepsDismissed && connectedOrganizationEmail && (
				<ConnectionNextStepsCard
					clientId={clientId}
					connectedEmail={connectedOrganizationEmail}
				/>
			)}

			<div className="flex flex-col gap-3">
				<div className="flex items-center gap-3">
					<Mail className="h-4 w-4 text-muted-foreground shrink-0" />
					<div className="text-sm">
						<span className="text-muted-foreground">E-Mail: </span>
						<span className="font-medium">
							{connectedOrganizationEmail}
						</span>
					</div>
				</div>

				{connectedOrganizationType && (
					<div className="flex items-center gap-3">
						<Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
						<div className="text-sm">
							<span className="text-muted-foreground">Typ: </span>
							<span className="font-medium">
								{connectedOrganizationType === "med"
									? "Praxis"
									: "Labor"}
							</span>
						</div>
					</div>
				)}

				{connectRelationshipForMultipleClients && (
					<div className="flex items-center gap-3">
						<Users className="h-4 w-4 text-muted-foreground shrink-0" />
						<span className="text-sm text-muted-foreground">
							Diese Verbindung besteht zu mehreren Auftraggebern.
						</span>
					</div>
				)}
			</div>

			<div>
				<Button
					variant="outline"
					size="sm"
					onClick={() => setWarningDialogOpen(true)}
					aria-label="Verbindung auflösen"
					data-testid="disconnect-client-button"
				>
					<Unplug className="h-4 w-4 mr-2" />
					{connectRelationshipForMultipleClients
						? "Diesen Auftraggeber trennen"
						: "Verbindung auflösen"}
				</Button>
			</div>

			<Dialog
				open={warningDialogOpen}
				onOpenChange={setWarningDialogOpen}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>
							Wollen Sie den Zugang wirklich aufheben?
						</DialogTitle>
						<DialogDescription>
							Alle geteilten Daten bleiben erhalten, aber werden
							nicht mehr geteilt — auch wenn Sie den Zugang später
							wieder herstellen.
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setWarningDialogOpen(false)}
						>
							Abbrechen
						</Button>
						<Button
							variant="destructive"
							onClick={() => {
								// disconnect client currently reloads the page so we capture the event before
								postHog.capture("disconnect_client");
								disconnectClient();
							}}
						>
							Verbindung trennen
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
};
